/**
 * /coupon-tracking — coupon code tracking workspace (FS-825).
 *
 * Primary route file for the coupon-tracking dash feature. Mounts
 * `CouponTrackingPage` at `/coupon-tracking`. The existing namespaced route
 * `tracking.coupons.tsx` already mounts the same page at `/tracking/coupons`;
 * this file provides the canonical slug-matched mount required by D1.
 *
 * Search params:
 *   ?mode=general|exclusive|unique   Pre-filter the list by coupon type.
 *   ?couponId=cpn_xxx                Focus a specific coupon.
 *   ?campaignId=cmp_xxx              Pre-select a campaign.
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import {
	COUPON_MODES,
	type CouponMode,
} from "@/features/coupon-tracking/api";
import { CouponTrackingPage } from "@/features/coupon-tracking/CouponTrackingPage";

// ---------------------------------------------------------------------------
// Search param validation
// ---------------------------------------------------------------------------

interface CouponTrackingSearch {
	mode?: CouponMode;
	couponId?: string;
	campaignId?: string;
}

function coerceMode(raw: unknown): CouponMode | undefined {
	if (typeof raw !== "string") return undefined;
	const trimmed = raw.trim().toLowerCase();
	if ((COUPON_MODES as readonly string[]).includes(trimmed)) {
		return trimmed as CouponMode;
	}
	return undefined;
}

function coerceStr(raw: unknown, max = 120): string | undefined {
	if (typeof raw !== "string") return undefined;
	const trimmed = raw.trim();
	if (!trimmed || trimmed.length > max) return undefined;
	return trimmed;
}

// ---------------------------------------------------------------------------
// Route — mounts at `/coupon-tracking`.
// ---------------------------------------------------------------------------

export const couponTrackingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/coupon-tracking",
	validateSearch: (raw: Record<string, unknown>): CouponTrackingSearch => {
		const out: CouponTrackingSearch = {};
		const mode = coerceMode(raw.mode);
		if (mode) out.mode = mode;
		const couponId = coerceStr(raw.couponId);
		if (couponId) out.couponId = couponId;
		const campaignId = coerceStr(raw.campaignId);
		if (campaignId) out.campaignId = campaignId;
		return out;
	},
	component: CouponTrackingRouteComponent,
});

export default couponTrackingRoute;

// Wrapper reads search params so the page can deep-link without prop
// drilling from the router call site.
export function CouponTrackingRouteComponent() {
	const { mode, couponId, campaignId } = couponTrackingRoute.useSearch();
	return (
		<CouponTrackingPage
			initialMode={mode}
			initialCouponId={couponId}
			initialCampaignId={campaignId}
		/>
	);
}

// Re-export the page for the root wire-up cycle.
export { CouponTrackingPage } from "@/features/coupon-tracking/CouponTrackingPage";
